import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { FormContext, selectErrorMessage } from './Form';

const RequiredMarker = ({ title }) => (
  <svg
    width={14}
    height={13}
    viewBox="0 0 14 13"
    xmlns="http://www.w3.org/2000/svg"
    fillRule="evenodd"
    clipRule="evenodd"
    strokeLinejoin="round"
    strokeMiterlimit={2}
    className="ss_label_required"
  >
    <title>{title}</title>
    <path
      d="M6.657 0L8.23 4.837h5.085L9.2 7.826l1.571 4.837-4.114-2.989-4.114 2.989 1.571-4.837L0 4.837h5.085L6.657 0z"
      fill="#4ec3f7"
    />
  </svg>
);

RequiredMarker.propTypes = {
  title: PropTypes.string.isRequired,
};

const Field = ({
  label,
  name,
  type,
  value,
  readOnly,
  disabled,
  maxLength,
  pattern,
  title,
  required,
  hint,
  onChange,
  defaultValue,
  className,
}) => {
  const {
    submitting,
    errors,
    errorMessages,
    phrases: { required: requiredTitle },
  } = useContext(FormContext);

  if (type === 'hidden') {
    return <input type={type} name={name} value={value} />;
  }

  return (
    <label
      className={clsx(
        'ss_field',
        `ss_field--${type}`,
        `ss_field--${name}`,
        { 'ss_field--error': errors[name] !== undefined },
        className,
      )}
    >
      <div className="ss_label">
        {label}
        {required && <RequiredMarker title={requiredTitle} />}
      </div>
      <input
        className="ss_input"
        type={type}
        name={name}
        disabled={disabled || submitting}
        readOnly={readOnly}
        maxLength={maxLength}
        pattern={pattern}
        title={title || label}
        required={required}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
      />
      {type === 'checkbox' && <span className="ss_input ss_input--pseudo" />}
      {errors[name] && (
        <span className="ss_field__error_message">
          {selectErrorMessage(errorMessages, name, errors[name])}
        </span>
      )}
      {hint && <div className="ss_field_hint">{hint}</div>}
    </label>
  );
};

Field.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'password',
    'email',
    'number',
    'password',
    'search',
    'tel',
    'time',
    'checkbox',
    'hidden',
  ]),
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  pattern: PropTypes.string,
  title: PropTypes.string,
  required: PropTypes.bool,
  hint: PropTypes.node,
  onChange: PropTypes.func,
  defaultValue: PropTypes.string,
  className: PropTypes.string,
};

Field.defaultProps = {
  type: 'text',
  label: undefined,
  readOnly: false,
  disabled: false,
  maxLength: undefined,
  pattern: undefined,
  title: undefined,
  required: false,
  hint: undefined,
  value: undefined,
  onChange: undefined,
  defaultValue: undefined,
  className: undefined,
};

export { Field };
